import React, { useState, useEffect } from "react";
import apiService from "../config";
import { Send, FileText, Save, Eye, Upload, X } from 'lucide-react';
import { toast } from "react-toastify";
import { useLanguage } from "../StoreContext/LanguageContext";
import LandAcquisitionNotice from './CompensationNotices';
import SignaturePad from '../DigitalSignature/SignaturePad';

const WorkersJudgment = ({ onBack, projectId, setSubComponent }) => {
    const { language, translations } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProjectName, setCurrentProjectName] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);
    const [userRole, setUserRole] = useState(null);

    // 37(2) Modal
    const [showCompensationNotice, setShowCompensationNotice] = useState(false);
    const [selectedOwnerId, setSelectedOwnerId] = useState(null);

    // DSLR Modal States
    const [showSendToDSLRModal, setshowSendToDSLRModal] = useState(false);
    const [selectedDocumentDSLR, setselectedDocumentDSLR] = useState(null);
    const [remarksDSLR, setremarksDSLR] = useState('Approved');
    const [sendingToDSLR, setsendingToDSLR] = useState(false);
    const [usersDSLR, setusersDSLR] = useState([]);
    const [loadingusersDSLR, setLoadingusersDSLR] = useState(false);
    const [selectedUserDSLR, setselectedUserDSLR] = useState('');
    
    // --- Document Viewer Modal States ---
    const [showDocumentViewer, setShowDocumentViewer] = useState(false);
    const [fileUrlToView, setFileUrlToView] = useState('');
    const [isPDFToView, setIsPDFToView] = useState(false);

    // --- Digital Signature States ---
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);
    const [signStatus, setSignStatus] = useState('idle'); // 'idle' | 'signing' | 'success' | 'error'
    const [signingContext, setSigningContext] = useState(null); // { type: 'dslr' } OR { type: 'owner', id: '...' }

    // Button Status
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        type: 'warning'
    });

    const [proposalData, setProposalData] = useState(null);

    // Table & Owner Data States
    const [landAcquisitionData, setLandAcquisitionData] = useState([]);
    const [ownerFiles, setOwnerFiles] = useState({}); // To track local files selected for upload per owner

    useEffect(() => {
        const getProjectId = () => {
            const currentProjectData = localStorage.getItem("currentProjectData");
            if (currentProjectData) {
                const projectData = JSON.parse(currentProjectData);
                setCurrentProjectName(projectData.project_name || '');
                return projectData.project_id || projectData.id;
            }
            const userData = localStorage.getItem("userData");
            if (userData) {
                const user = JSON.parse(userData);
                return user.data?.project_id || user.project_id;
            }
            return null;
        };

        const getProposalId = () => {
            const userData = localStorage.getItem("userData");
            if (userData) {
                const user = JSON.parse(userData);
                return user.default_proposal;
            }
            return null;
        };

        const projectId = getProjectId();
        const proposalId = getProposalId();

        setCurrentProjectId(projectId);
        setCurrentProposalId(proposalId);
    }, []);

    // Get user role
    useEffect(() => {
        try {
            const userData = localStorage.getItem("userData");
            if (userData) {
                const user = JSON.parse(userData);
                setUserRole(user.user_role || user.user_role_name || null);
            }
        } catch (err) {
            console.error("Error parsing user data:", err);
        }
    }, []);

    // Fetch Proposal Data
    const fetchProposalData = () => {
        try {
            const storedProposal = JSON.parse(localStorage.getItem('selectedProposal') || '{}');
            setProposalData(storedProposal || {});
        } catch (error) {
            console.error("Error fetching proposal data:", error);
        }
    };

    useEffect(() => {
        fetchusersDSLR();
        fetchProposalData();
    }, []);

    useEffect(() => {
        if (currentProjectId && currentProposalId) {
            fetchButtonStatus();
            fetchMeasurementData();
        }
    }, [currentProjectId, currentProposalId]);

    // Fetch Measurement / Owner Data API
    const fetchMeasurementData = async () => {
        if (!currentProjectId || !currentProposalId) return;
        setLoading(true);

        try {
            const result = await apiService.getAllOwner(null, null, null, currentProjectId, currentProposalId);
            
            if (result.data && result.data.length > 0) {
                const allOwners = result.data.flatMap(acquisition => acquisition.owners || []);
                setLandAcquisitionData(allOwners);

                const firstRecord = result.data[0];
                setProposalData(prev => ({
                    ...prev,
                    section_11_bhusampadan_no: firstRecord.section_11_bhusampadan_no || '',
                    acquisition_incharge_designation: firstRecord.acquisition_incharge_designation || '',
                    acquisition_purpose: firstRecord.acquisition_purpose || '',
                    village_name: firstRecord.village_name || '',
                    taluka_name: firstRecord.taluka_name || '',
                    dist_name: firstRecord.dist_name || ''
                }));
                fetchButtonStatus();
            } else {
                setLandAcquisitionData([]);
            }
        } catch (error) {
            console.error('Error in fetchMeasurementData:', error);
            setLandAcquisitionData([]);
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (title, message, type) => {
        if (type === 'error') {
            toast.error(`${title}: ${message}`);
        } else {
            toast.success(`${title}: ${message}`);
        }
    };

    const handleFileChange = (ownerId, file) => {
        if (!file) return;
        setOwnerFiles(prev => ({
            ...prev,
            [ownerId]: file
        }));
        setSignStatus('idle'); // Reset sign status when a new file is uploaded
    };

    // --- Action: Cancel Row File Selection ---
    const handleCancelRowSelection = (ownerId) => {
        setOwnerFiles(prev => {
            const updated = { ...prev };
            delete updated[ownerId];
            return updated;
        });
        setSignStatus('idle');
    };

    // --- Document Viewer & Sign Actions ---
    const handleViewDocument = (urlOrFile) => {
        if (!urlOrFile) return;

        let url;
        let isPdf;

        if (urlOrFile instanceof File) {
            url = URL.createObjectURL(urlOrFile);
            isPdf = urlOrFile.type === 'application/pdf';
        } else {
            url = urlOrFile;
            isPdf = urlOrFile.toLowerCase().includes('.pdf');
        }

        setFileUrlToView(url);
        setIsPDFToView(isPdf);
        setShowDocumentViewer(true);
    };

    const closeDocumentViewer = () => {
        setShowDocumentViewer(false);
        if (fileUrlToView.startsWith('blob:')) {
            URL.revokeObjectURL(fileUrlToView);
        }
        setFileUrlToView('');
        setIsPDFToView(false);
    };

    const signDocument = async (file, signaturesArray) => {
        setSignStatus('signing');
        try {
            // Simulated signing delay - Replace with actual API/Signing logic
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Route the signed document to the correct state variable based on context
            if (signingContext?.type === 'dslr') {
                setselectedDocumentDSLR(file);
            } else if (signingContext?.type === 'owner' && signingContext?.id) {
                setOwnerFiles(prev => ({ ...prev, [signingContext.id]: file }));
            }
            
            setSignStatus('success');
            setTimeout(() => setSignStatus('idle'), 3000); // clear status message
        } catch (err) {
            setSignStatus('error');
            toast.error("Failed to sign document");
        }
    };

    // *** API UPLOAD LOGIC ***
    const handleUpdateMeasurement = async (ownerId) => {
        const fileToUpload = ownerFiles[ownerId];

        if (!ownerId) {
            showAlert('Error', 'Land Owner ID is missing. Cannot upload document.', 'error');
            return;
        }
        if (!fileToUpload) {
            showAlert('Error', 'Please select a file to upload first.', 'warning');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                afterAwardFile: fileToUpload
            };

            const response = await apiService.patchUpdateMeasurement(ownerId, payload);
            
            if (response.status === 200 || response.status === 201 || response.status === 204) {
                showAlert('Success', 'Document uploaded successfully!', 'success');
                
                setOwnerFiles(prev => {
                    const updated = { ...prev };
                    delete updated[ownerId];
                    return updated;
                });
                
                await fetchMeasurementData();
            } else {
                showAlert('Error', 'Failed to upload document. Please try again.', 'error');
            }
        } catch (error) {
            console.error("Error uploading document:", error);
            showAlert('Error', 'Error occurred while uploading document. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // fetch DSLR users
    const fetchusersDSLR = async () => {
        setLoadingusersDSLR(true);
        try {
            const response = await apiService.getAllUserRoleDSLR(15, currentProjectId, currentProposalId);
            if (response.status === 200 && response.data) {
                setusersDSLR(Array.isArray(response.data) ? response.data : []);
            } else {
                setusersDSLR([]);
            }
        } catch (error) {
            console.error('Error fetching DSLR users:', error);
            setusersDSLR([]);
        } finally {
            setLoadingusersDSLR(false);
        }
    };

    const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
        setConfirmConfig({ title, message, onConfirm, onCancel, type });
        setShowConfirmModal(true);
    };

    // Handle Send to DSLR
    const handleSendToDSLR = async () => {
        if (!selectedUserDSLR || !selectedDocumentDSLR) {
            alert('Please select a user and upload a document to send to DSLR Officer.');
            return;
        }

        if (!currentProjectId || !currentProposalId) {
            alert('Project ID and Proposal ID are required.');
            return;
        }

        setsendingToDSLR(true);

        try {
            const formData = new FormData();
            formData.append('project_id', currentProjectId);
            formData.append('proposal_id', currentProposalId);
            formData.append('remarks', remarksDSLR);
            formData.append('usr_id', selectedUserDSLR);
            formData.append('document', selectedDocumentDSLR);

            const response = await apiService.postTrackingDslrKjp(formData);

            if (response.status === 200) {
                alert(translations[language]?.SuccessfullySentToDSLROfficer || "Successfully sent to DSLR Officer!");
                setshowSendToDSLRModal(false);
                setselectedUserDSLR('');
                setremarksDSLR('Approved');
                setselectedDocumentDSLR(null);
                setSignStatus('idle');
                fetchButtonStatus();
            } else {
                alert('Failed to send to DSLR Officer. Please try again.');
            }
        } catch (error) {
            console.error('Error sending to DSLR:', error);
            alert('Failed to send to DSLR Officer. Please try again.');
        } finally {
            setsendingToDSLR(false);
        }
    };

    const fetchButtonStatus = async () => {
        try {
            if (!currentProjectId || !currentProposalId) return;
            let response = await apiService.getDslrKjpButton(currentProjectId, currentProposalId);
            setButtonDisabled(!response.buttonDisable);
        } catch (error) {
            console.error("Error fetching DSLR button state:", error);
        }
    };

    // Group owners by village
    const groupedOwners = landAcquisitionData.reduce((acc, owner) => {
        const village = owner.village_name || 'Unknown Village';
        if (!acc[village]) {
            acc[village] = [];
        }
        acc[village].push(owner);
        return acc;
    }, {});

    return (
        <div className="flex flex-col w-full">
            {/* Top Buttons Section */}
            <div className="p-4 bg-white flex justify-between items-center shadow-sm">
                <div className="flex gap-3">
                    <button
                        className="px-4 py-2 text-sm rounded-md shadow-sm transition-all duration-200 bg-yellow-700 hover:bg-yellow-800 text-white flex items-center"
                        onClick={() => {
                            setSelectedOwnerId('all'); 
                            setShowCompensationNotice(true); 
                        }}
                    >
                        <FileText className="h-4 w-4 mr-2" />
                        {translations[language]?.kalam37_2 || 'Kalam 37(2)'}
                    </button>

                    <button
                        className="px-4 py-2 text-sm rounded-md shadow-sm transition-all duration-200 bg-yellow-700 hover:bg-yellow-800 text-white"
                        onClick={() => setSubComponent("KabjaPauti")}
                    >
                        {translations[language]?.KabjaPauti || 'Kabja Pauti'}
                    </button>

                    <button
                        className="px-4 py-2 text-sm rounded-md shadow-sm transition-all duration-200 bg-yellow-700 hover:bg-yellow-800 text-white"
                        onClick={() => setSubComponent("FormC")}
                    >
                        {translations[language]?.formSell || 'Form Sell'}
                    </button>

                    <button
                        className="px-4 py-2 text-sm rounded-md shadow-sm transition-all duration-200 bg-yellow-700 hover:bg-yellow-800 text-white"
                        onClick={() => setSubComponent("LandAcquisitionCerti")}
                    >
                        {translations[language]?.LandAcquisitionCerti || 'Land Acquisition Certi'}
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 p-3 lg:ml-auto border border-purple-200 bg-purple-50 rounded-md shadow-sm min-w-max">
                    {userRole === 3 && (
                        <button
                            type="button"
                            onClick={() => {
                                fetchusersDSLR();
                                if (buttonDisabled) {
                                    showConfirm(
                                        translations[language]?.confirmResubmission || 'Confirm Resubmission',
                                        translations[language]?.resubmissionWarning || 'Are you sure you want to resubmit?',
                                        () => setshowSendToDSLRModal(true),
                                        () => console.log("Resend cancelled"),
                                        "danger" 
                                    );
                                } else {
                                    setshowSendToDSLRModal(true);
                                }
                            }}
                            className={`flex items-center px-4 py-2 text-sm rounded-md transition-colors ${buttonDisabled
                                ? "bg-red-600 text-white hover:bg-red-700"
                                : "bg-purple-600 text-white hover:bg-purple-700"
                                }`}
                            title={
                                buttonDisabled
                                    ? translations[language]?.AlreadySentToDSLROfficerClickToResend || 'Already Sent. Click to Resend'
                                    : translations[language]?.SendToDSLROfficer || 'Send to DSLR Officer'
                            }
                        >
                            <Send className="mr-2 h-4 w-4" />
                            <span>{translations[language]?.sendToKJP || 'Send to KJP'}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Owner Data Table Section */}
            <div className="p-4">
                <div className="bg-white rounded-md shadow-md overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-3">Owner Name</th>
                                    <th className="px-6 py-3">District</th>
                                    <th className="px-6 py-3">Taluka</th>
                                    <th className="px-6 py-3">Village</th>
                                    <th className="px-6 py-3 text-center">afterAwardFile</th>
                                    <th className="px-6 py-3">View Document</th>
                                    <th className="px-6 py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && landAcquisitionData.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center">Loading owners data...</td>
                                    </tr>
                                ) : landAcquisitionData.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center">No owner records found.</td>
                                    </tr>
                                ) : (
                                    Object.keys(groupedOwners).map(villageName => (
                                        <React.Fragment key={villageName}>
                                            <tr className="bg-gray-100 border-b">
                                                <td colSpan="7" className="px-6 py-2 font-bold text-gray-800">
                                                    Village: {villageName}
                                                </td>
                                            </tr>

                                            {groupedOwners[villageName].map((row, index) => {
                                                const localFile = ownerFiles[row.land_owner_id];
                                                const serverFileUrl = row.afterAwardFile || row.document_url || row.document;

                                                return (
                                                    <tr key={row.land_owner_id || index} className="bg-white border-b hover:bg-gray-50">
                                                        <td className="px-6 py-4 font-medium text-gray-900 pl-10">{row.owner_name || 'N/A'}</td>
                                                        <td className="px-6 py-4">{row.dist_name || 'N/A'}</td>
                                                        <td className="px-6 py-4">{row.taluka_name || 'N/A'}</td>
                                                        <td className="px-6 py-4">{row.village_name || 'N/A'}</td>

                                                        <td className="px-6 py-4 text-center">
                                                            <div className="flex flex-col items-center gap-2">
                                                                <label className="cursor-pointer bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1 rounded-md border border-blue-200 flex items-center gap-1 transition-colors text-xs">
                                                                    <Upload size={14} /> Upload PDF/Img
                                                                    <input
                                                                        type="file"
                                                                        className="hidden"
                                                                        accept=".pdf,image/*"
                                                                        onChange={(e) => handleFileChange(row.land_owner_id, e.target.files[0])}
                                                                    />
                                                                </label>

                                                                {localFile && (
                                                                    <div className="flex flex-col items-center gap-2 mt-1">
                                                                        <span className="text-xs text-green-600 truncate max-w-[100px]" title={localFile.name}>
                                                                            {localFile.name}
                                                                        </span>
                                                                      
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                              {serverFileUrl && !localFile && (
                                                                <button
                                                                    onClick={() => handleViewDocument(serverFileUrl)}
                                                                    className="text-gray-600 hover:text-blue-600 inline-flex items-center justify-center gap-1 text-xs mt-1"
                                                                    title="View Uploaded File"
                                                                >
                                                                    <Eye size={14} /> View Document
                                                                </button>
                                                            )}
                                                        </td>

                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <button
                                                                    onClick={() => handleUpdateMeasurement(row.land_owner_id)}
                                                                    disabled={!localFile || loading}
                                                                    className={`px-4 py-2 text-sm rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${
                                                                        localFile
                                                                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md cursor-pointer'
                                                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    }`}
                                                                    title="Save Document"
                                                                >
                                                                    <Save size={16} /> {translations[language]?.CommonSave || 'Save'}
                                                                </button>
                                                                
                                                                {/* Cancel Row Selection Button */}
                                                                {localFile && (
                                                                    <button
                                                                        onClick={() => handleCancelRowSelection(row.land_owner_id)}
                                                                        className="px-3 py-2 text-sm bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-md transition-all duration-200 flex items-center justify-center border border-red-200 shadow-sm"
                                                                        title="Cancel/Remove File"
                                                                    >
                                                                        <X size={16} /> Cancel
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Send to DSLR Modal with Rich Upload UI */}
            {showSendToDSLRModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.SendToDSLR || 'Send to DSLR'} *
                                    </label>
                                    <select
                                        value={selectedUserDSLR}
                                        onChange={(e) => setselectedUserDSLR(e.target.value)}
                                        disabled={loadingusersDSLR}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">
                                            {loadingusersDSLR ? translations[language]?.LoadingUsers || 'Loading...' : translations[language]?.SelectUser || 'Select User'}
                                        </option>
                                        {usersDSLR.map((user) => (
                                            <option key={user.usr_id} value={user.usr_id}>
                                                {user.full_name} ({user.usr_role})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translations[language]?.Remarks || 'Remarks'}
                                    </label>
                                    <textarea
                                        value={remarksDSLR}
                                        onChange={(e) => setremarksDSLR(e.target.value)}
                                        rows={3}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter remarks..."
                                    />
                                </div>

                                {/* Rich Document Upload for DSLR Modal */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload Document *</label>
                                    <input
                                        type="file"
                                        onChange={(e) => {
                                            setselectedDocumentDSLR(e.target.files[0]);
                                            setSignStatus('idle'); // Reset sign state on new file
                                        }}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    />
                                    {selectedDocumentDSLR && (
                                        <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm text-purple-700 font-medium truncate max-w-[60%]">
                                                    Selected: {selectedDocumentDSLR.name}
                                                </p>
                                                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                                    {(selectedDocumentDSLR.size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                            </div>
                                            
                                            <div className="flex gap-2">
                                                {selectedDocumentDSLR.type === 'application/pdf' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPdfForSigning(selectedDocumentDSLR);
                                                            setSigningContext({ type: 'dslr' });
                                                            setShowSignaturePad(true);
                                                        }}
                                                        disabled={signStatus === 'signing'}
                                                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                                                    >
                                                        {signStatus === 'signing' ? 'Signing...' : 'Sign Doc'}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewDocument(selectedDocumentDSLR)}
                                                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                                                >
                                                    <Eye className="h-3 w-3" /> View
                                                </button>
                                            </div>

                                            {signStatus === 'success' && signingContext?.type === 'dslr' && (
                                                <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                                                    ✅ Document signed successfully!
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                                <button
                                    onClick={() => {
                                        setshowSendToDSLRModal(false);
                                        setselectedDocumentDSLR(null);
                                        setSignStatus('idle');
                                    }}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    disabled={sendingToDSLR}
                                >
                                    {translations[language]?.Cancel || 'Cancel'}
                                </button>
                                <button
                                    onClick={handleSendToDSLR}
                                    disabled={sendingToDSLR || !selectedUserDSLR || !selectedDocumentDSLR}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                                >
                                    {sendingToDSLR ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {translations[language]?.sending || 'Sending...'}
                                        </>
                                    ) : (
                                        <>
                                            <Send className="mr-2 h-4 w-4" />
                                            {translations[language]?.CommonSave || 'Save'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
                        <div className="p-6">
                            <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${
                                confirmConfig.type === 'danger' ? 'bg-red-100' :
                                confirmConfig.type === 'warning' ? 'bg-yellow-100' : 'bg-blue-100'
                            }`}>
                                {confirmConfig.type === 'danger' ? '⚠' : confirmConfig.type === 'warning' ? '⚠' : '?'}
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                                {confirmConfig.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-6 text-center">
                                {confirmConfig.message}
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onCancel) confirmConfig.onCancel();
                                    }}
                                    className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    {translations[language]?.Cancel || 'Cancel'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                                    }}
                                    className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${
                                        confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                                        confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                        'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {translations[language]?.confirm || 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SignaturePad Modal Wrapper */}
            {showSignaturePad && pdfForSigning && (
                <SignaturePad
                    pdfFile={pdfForSigning}
                    onSign={(signaturesArray) => {
                        setShowSignaturePad(false);
                        signDocument(pdfForSigning, signaturesArray).then(() => {
                            setPdfForSigning(null);
                        });
                    }}
                    onCancel={() => {
                        setShowSignaturePad(false);
                        setPdfForSigning(null);
                        setSigningContext(null);
                    }}
                />
            )}

            {/* Unified View Document Modal */}
            {showDocumentViewer && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl w-full max-w-5xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <div className="flex items-center space-x-3">
                                <h3 className="text-2xl font-bold text-gray-800 tracking-tight">Document Preview</h3>
                            </div>
                            <button
                                onClick={closeDocumentViewer}
                                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-all duration-200 flex items-center justify-center text-xl font-bold shadow-sm hover:shadow-md"
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>

                        {/* Document Viewer Container */}
                        <div className="h-[calc(95vh)] flex flex-col">
                            <div className="flex-1 bg-gray-50 overflow-hidden shadow-inner border border-gray-200">
                                {/* Document Content */}
                                <div className="w-full h-full flex items-center justify-center">
                                    {isPDFToView ? (
                                        <iframe
                                            src={fileUrlToView}
                                            className="w-full h-full border-0 bg-white shadow-lg"
                                            title="PDF Preview"
                                        ></iframe>
                                    ) : (
                                        <img
                                            src={fileUrlToView}
                                            alt="File"
                                            className="w-full h-full object-contain shadow-lg"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Compensation Notices */}
            {showCompensationNotice && (
                <LandAcquisitionNotice
                    ownerId={selectedOwnerId} 
                    currentProjectName={currentProjectName}
                    currentProjectId={currentProjectId}
                    currentProposalId={currentProposalId}
                    isVisible={showCompensationNotice}
                    proposalData={proposalData}
                    onClose={() => {
                        setShowCompensationNotice(false);
                        setSelectedOwnerId(null);
                    }}
                />
            )}
        </div>
    );
};

export default WorkersJudgment;

